import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Ticket, 
  Gift, 
  Star, 
  CalendarDays,
  Trophy,
  Sparkles,
  ChevronRight,
  MapPin,
  Clock,
  QrCode,
  Scissors
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";

const Lifestyle = ({ user }) => {
  const navigate = useNavigate();

  // Fetch featured events
  const { data: featuredData } = useQuery({
    queryKey: ["featuredEvents"],
    queryFn: async () => {
      const response = await axios.get(`${API}/events/featured?limit=3`);
      return response.data;
    }
  });

  // Fetch user's upcoming tickets
  const { data: ticketsData } = useQuery({
    queryKey: ["myTicketsPreview"],
    queryFn: async () => {
      const response = await axios.get(`${API}/tickets/my-tickets?upcoming_only=true`);
      return response.data;
    }
  });

  const featuredEvents = featuredData?.events || [];
  const upcomingTickets = ticketsData?.events || [];
  const totalTickets = ticketsData?.total_tickets || 0;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      weekday: "short",
      month: "short", 
      day: "numeric"
    });
  };

  const upcomingFeatures = [
    {
      icon: Gift,
      title: "Rewards Store",
      description: "Redeem points for cashback and partner perks",
      status: "Coming Soon"
    },
    {
      icon: Trophy,
      title: "Achievements",
      description: "Earn badges for saving, investing, and referring friends",
      status: "Coming Soon"
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="lifestyle-page">
      <header className="px-5 pt-4 pb-2 max-w-lg mx-auto">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-semibold text-xl text-white">Lifestyle</h1>
        </div>
      </header>

      <main className="px-5 max-w-lg mx-auto space-y-6">
        {/* Events & Tickets Hero */}
        <div className="glass rounded-3xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Events & Tickets</h2>
                <p className="text-sm text-white/80">Jamaica's hottest events</p>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <Button
                onClick={() => navigate("/events")}
                className="flex-1 bg-white text-purple-600 hover:bg-white/90 font-semibold"
                data-testid="browse-events-btn"
              >
                Browse Events
              </Button>
              <Button
                onClick={() => navigate("/my-tickets")}
                variant="outline"
                className="flex-1 border-white/30 text-white hover:bg-white/10"
                data-testid="my-tickets-btn"
              >
                <QrCode className="w-4 h-4 mr-2" />
                My Tickets ({totalTickets})
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          {totalTickets > 0 && (
            <div className="p-4 border-t border-white/10">
              <p className="text-xs text-muted-foreground mb-2">Upcoming Events</p>
              <div className="space-y-2">
                {upcomingTickets.slice(0, 2).map((event) => (
                  <button
                    key={event.event_id}
                    onClick={() => navigate("/my-tickets")}
                    className="w-full flex items-center justify-between p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                        <Ticket className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-white line-clamp-1">{event.event_name}</p>
                        <p className="text-xs text-muted-foreground">{event.tickets.length} ticket(s)</p>
                      </div>
                    </div>
                    <span className="text-xs text-emerald-400">{formatDate(event.event_date)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Stelx Points */}
        <div className="glass rounded-2xl p-5 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Stelx Points</p>
                <p className="text-xs text-muted-foreground">Earn rewards</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-white">0</p>
              <p className="text-xs text-muted-foreground">points</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Earn points by purchasing tickets, saving, and referring friends. Points system launching soon!
          </p>
        </div>

        {/* Split Bill Feature */}
        <div className="glass rounded-2xl p-5 border border-white/10">
          <button
            onClick={() => navigate("/split-bill")}
            className="w-full flex items-center justify-between"
            data-testid="split-bill-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                <Scissors className="w-5 h-5 text-teal-400" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-white">Split Bill</p>
                <p className="text-xs text-muted-foreground">Share expenses with friends</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Featured Events */}
        {featuredEvents.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" />
                Featured Events
              </h3>
              <button 
                onClick={() => navigate("/events")}
                className="text-xs text-emerald-400 hover:text-emerald-300"
              >
                See all
              </button>
            </div>
            <div className="space-y-3">
              {featuredEvents.map((event) => (
                <button
                  key={event.event_id}
                  onClick={() => navigate(`/events/${event.event_id}`)}
                  className="w-full flex gap-4 p-3 rounded-xl bg-white/5 border border-white/10 text-left hover:bg-white/10 transition-colors"
                  data-testid={`featured-${event.event_id}`}
                >
                  {(event.flyer_url || event.image_url) ? (
                    <img 
                      src={event.flyer_url || event.image_url} 
                      alt={event.name}
                      className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500/50 to-pink-500/50 flex items-center justify-center flex-shrink-0">
                      <Ticket className="w-6 h-6 text-white/50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white line-clamp-1">{event.name}</p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                      <CalendarDays className="w-3 h-3" />
                      <span>{formatDate(event.event_date)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                      <MapPin className="w-3 h-3" />
                      <span className="line-clamp-1">{event.parish}</span>
                    </div>
                    <p className="text-sm font-semibold text-emerald-400 mt-1">
                      From J${event.starting_price?.toLocaleString() || "Free"}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0 self-center" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Coming Soon */}
        <div>
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-400" />
            Coming Soon
          </h3>
          <div className="space-y-3">
            {upcomingFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10 opacity-70"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{feature.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
                  </div>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-white/10 text-muted-foreground">
                    {feature.status}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <NavBar />
    </div>
  );
};

export default Lifestyle;
